"use client";

import { useEffect } from "react";
import type { ToasterProps } from "sonner";
import { Toaster as ExternalToaster } from "sonner";
import { twMerge } from "tailwind-merge";
import {
  DEFAULT_TOAST_DURATION,
  DEFAULT_TOASTER_ID,
  DEFAULT_VISIBLE_TOASTS,
} from "@/constants";
import type { ToasterId } from "@/types";
import { registerToaster, unregisterToaster } from "./registry";

export function Toaster({
  id = DEFAULT_TOASTER_ID,
  className,
  position = "bottom-center",
  duration = DEFAULT_TOAST_DURATION,
  richColors = false,
  theme = "system",
  offset = 32,
  mobileOffset = 20,
  visibleToasts = DEFAULT_VISIBLE_TOASTS,
  gap = 8,
  absolute = true,
  ...rest
}: ToasterProps & { id?: ToasterId; absolute?: boolean }) {
  useEffect(() => {
    registerToaster(id);
    return () => unregisterToaster(id);
  }, [id]);

  return (
    <ExternalToaster
      id={id}
      className={twMerge(absolute && "!absolute", className)}
      position={position}
      duration={duration}
      richColors={richColors}
      theme={theme}
      offset={offset}
      mobileOffset={mobileOffset}
      visibleToasts={visibleToasts}
      gap={gap}
      toastOptions={{
        classNames: {
          toast:
            "flex items-center gap-3 rounded-toast border border-ui-toast-border bg-ui-toast-bg text-ui-toast-fg shadow-toast " +
            "data-[type=success]:border-ui-toast-success-border data-[type=success]:bg-ui-toast-success-bg " +
            "dark:data-[type=success]:border-ui-toast-success-border/40 dark:data-[type=success]:bg-ui-toast-success-bg/20 " +
            "data-[type=error]:border-ui-toast-error-border data-[type=error]:bg-ui-toast-error-bg " +
            "dark:data-[type=error]:border-ui-toast-error-border/40 dark:data-[type=error]:bg-ui-toast-error-bg/20 " +
            "data-[type=warning]:border-ui-toast-warning-border data-[type=warning]:bg-ui-toast-warning-bg " +
            "dark:data-[type=warning]:border-ui-toast-warning-border/40 dark:data-[type=warning]:bg-ui-toast-warning-bg/20",
          title: "text-sm font-semibold",
          icon:
            "inline-flex shrink-0 w-fit min-w-fit [&_[data-slot=icon]]:mt-0.5 " +
            "[&_[data-slot=icon]]:size-7 sm:[&_[data-slot=icon]]:size-6",
          content: "relative flex-1 min-w-0",
          description: "text-xs text-ui-toast-description-fg",
          actionButton:
            "rounded-toast-action border border-ui-toast-action-border px-2.5 py-1 text-xs font-medium",
          cancelButton:
            "rounded-toast-action border border-ui-toast-action-border px-2.5 py-1 text-xs font-medium",
        },
      }}
      {...rest}
    />
  );
}
